import { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Button, Grid, Typography } from '@mui/material';
import { confirmResetPassword, signIn } from 'aws-amplify/auth';
import { codeResponseErrorsMap } from 'constants/confirmationCodeResponse';
import { Field, FieldProps, Form, Formik, FormikHelpers } from 'formik';

import usePromiseStatus from 'hooks/usePromiseStatus';

import { IStepFormProps as Props, StepQuantity } from 'interfaces/auth';

import { BackButton } from 'components/_basic/BackButton/BackButton';
import { CloseButton } from 'components/_basic/CloseButton/CloseButton';
import { FormField } from 'components/_basic/Form/FormField';
import { PasswordTextField } from 'components/_basic/PasswordTextField/PasswordTextField';
import { useAuthModalsContext } from 'components/AuthModals';
import { useResetPasswordContext } from 'components/ResetPassword/ResetPasswordContextProvider/ResetPasswordContextProvider';

import { resetPasswordSchema } from 'helpers/validations/signFormsValidation';

import styles from '../../ResetPassword.module.scss';

interface IFormInitialValues {
  password: string;
  confirmPassword: string;
}

export const EnterPasswordStep = ({ onClose }: Props) => {
  const { t } = useTranslation();
  const { prevStep, setCodeError } = useResetPasswordContext();
  const { resetFormData, setResetFormData, setIsModalOpened } = useAuthModalsContext();
  const { track, isPending } = usePromiseStatus();

  const initialValues: IFormInitialValues = {
    password: resetFormData.password,
    confirmPassword: resetFormData.confirmPassword,
  };

  const onSubmit = async ({ password }: IFormInitialValues, { resetForm }: FormikHelpers<IFormInitialValues>) => {
    try {
      await track(
        confirmResetPassword({
          confirmationCode: resetFormData.code,
          username: resetFormData.email,
          newPassword: password,
          options: { autoSignIn: true },
        }),
      );

      await track(signIn({ username: resetFormData.email, password }));

      setIsModalOpened(false);
      setCodeError('');
      resetForm();
    } catch (error) {
      if (error instanceof Error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Password reset/sign in failed:', {
            errorType: error.name,
          });
        }

        setCodeError(codeResponseErrorsMap.get(error.name) ?? error.message);
        prevStep();
      }
    }
  };

  const fieldChangeEvent = (
    { target: { value, name } }: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    setFieldValue: (field: string, value: string) => void,
  ): void => {
    setFieldValue(name, value);
    setResetFormData((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <Box>
      <Grid className={styles.formHeaderWrapper} container alignItems="center">
        <Grid item xs={11}>
          <BackButton
            onClick={(): void => {
              setResetFormData((prev) => ({ ...prev, code: '', password: '', confirmPassword: '' }));
              prevStep(StepQuantity.TWO);
            }}
          />
        </Grid>
        <Grid item xs={1} className={styles.closeButton}>
          <CloseButton onClick={onClose} />
        </Grid>
      </Grid>
      <Typography className={styles.formTitle} variant="h1" sx={{ marginBottom: '24px' }}>
        {t('newPassword')}
      </Typography>
      <Typography variant="body2" sx={{ marginBottom: '24px' }}>
        {t('resetPasswordRules')}
      </Typography>
      <Formik initialValues={initialValues} onSubmit={onSubmit} validationSchema={resetPasswordSchema}>
        {({ isValid, setFieldValue }) => (
          <Form>
            <Field name="password">
              {({ field, meta }: FieldProps<string>) => (
                <FormField
                  id={'resetPassword.password'}
                  label={t('password')}
                  error={meta.touched && meta.error}
                  sx={{ marginBottom: '24px' }}
                >
                  <PasswordTextField
                    {...field}
                    onChange={(event) => fieldChangeEvent(event, setFieldValue)}
                    inputProps={{ maxLength: 16 }}
                  />
                </FormField>
              )}
            </Field>
            <Field name="confirmPassword">
              {({ field, meta }: FieldProps<string>) => (
                <FormField
                  id={'resetPassword.confirmPassword'}
                  label={t('confirmPassword')}
                  error={meta.touched && meta.error}
                  sx={{ marginBottom: '24px' }}
                >
                  <PasswordTextField
                    {...field}
                    onChange={(event) => fieldChangeEvent(event, setFieldValue)}
                    inputProps={{ maxLength: 16 }}
                  />
                </FormField>
              )}
            </Field>
            <Button fullWidth variant="contained" type="submit" disabled={Boolean(!isValid) || isPending}>
              {t('createNewPassword')}
            </Button>
          </Form>
        )}
      </Formik>
    </Box>
  );
};
